import { MASAR } from "@/constants/testIds";
import { SectionLabel } from "@/components/masar/HowItWorks";
import { PLACES } from "@/components/masar/media";
import { useLanguage } from "@/i18n/LanguageContext";
import {
  Gauge,
  Map as MapIcon,
  Bike,
  Flag,
  UserCheck,
  Lock,
} from "lucide-react";

const cardsMeta = [
  { span: "col-span-6 lg:col-span-4", icon: Gauge },
  { span: "col-span-6 sm:col-span-3 lg:col-span-2", icon: Flag },
  { span: "col-span-6 sm:col-span-3 lg:col-span-2", icon: MapIcon },
  { span: "col-span-6 lg:col-span-4", icon: Bike, accent: true },
  { span: "col-span-6 sm:col-span-3 lg:col-span-3", icon: UserCheck },
  { span: "col-span-6 sm:col-span-3 lg:col-span-3", icon: Lock },
];

export default function Features() {
  const { t } = useLanguage();
  const cards = t("features.cards");
  return (
    <section
      id="features"
      data-testid={MASAR.sectionFeatures}
      className="relative py-24 sm:py-32 bg-neutral-950 text-white border-t border-neutral-800 overflow-hidden"
    >
      <div
        className="absolute inset-0 bg-cover bg-center opacity-[0.12] pointer-events-none"
        style={{ backgroundImage: `url(${PLACES.desertWadi})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-neutral-950 via-neutral-950/70 to-neutral-950 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        <div className="text-neutral-400">
          <SectionLabel index="05" label={t("features.chapter")} />
        </div>

        <div className="mt-4 grid lg:grid-cols-12 gap-8 items-end">
          <h2 className="lg:col-span-8 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-white">
            {t("features.titleA")}
            <br />
            {t("features.titleB")}
          </h2>
          <p className="lg:col-span-4 text-neutral-400 max-w-md">
            {t("features.lede")}
          </p>
        </div>

        <div className="mt-14 grid grid-cols-6 gap-4 sm:gap-5">
          {cardsMeta.map((m, i) => (
            <BentoCard
              key={cards[i].title}
              span={m.span}
              icon={m.icon}
              accent={!!m.accent}
              title={cards[i].title}
              tag={t("features.tag")}
            >
              {cards[i].body}
            </BentoCard>
          ))}
        </div>
      </div>
    </section>
  );
}

function BentoCard({ icon: Icon, title, children, span = "", accent = false, tag }) {
  return (
    <div
      className={`${span} relative rounded-3xl p-6 sm:p-7 border transition-all duration-300 group ${
        accent
          ? "bg-[#FF6A1A] text-white border-[#FF6A1A] hover:brightness-105"
          : "bg-neutral-900 text-white border-neutral-800 hover:border-neutral-600"
      }`}
    >
      <div className="flex items-center justify-between">
        <span
          className={`inline-flex h-11 w-11 items-center justify-center rounded-xl ${
            accent ? "bg-white/15 text-white" : "bg-white/10 text-white"
          }`}
        >
          <Icon size={20} />
        </span>
        <span className="text-[10px] font-mono uppercase tracking-widest opacity-70">{tag}</span>
      </div>
      <h3 className="mt-6 text-2xl font-semibold tracking-tight">{title}</h3>
      <p className={`mt-2 text-sm leading-relaxed ${accent ? "text-white/90" : "text-neutral-400"}`}>
        {children}
      </p>
    </div>
  );
}
